import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
  Dimensions,
  Animated,
} from 'react-native';
import { Wizard, useWizard } from 'react-use-wizard';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const WizardModalCloseContext = createContext<(() => void) | null>(null);

const WIZARD_STEPS = [
  {
    title: 'Get started',
    label: 'Get started',
    icon: 'person-outline' as const,
    contentTitle: 'Welcome to Color Analysis',
    contentBody:
      'Find your real undertone and a personalised palette that suits you.',
  },
  {
    title: 'Upload your photo',
    label: 'Upload your photo',
    icon: 'camera-outline' as const,
    contentTitle: 'Add your photo',
    contentBody:
      'Take a selfie or upload a photo in natural lighting for the best results.',
  },
  {
    title: 'View your palette',
    label: 'View your palette',
    icon: 'checkmark-circle-outline' as const,
    contentTitle: 'See your results',
    contentBody: 'View your undertone and recommended colours tailored to you.',
  },
];

const FLOW_DURATION = 400;

function StepIndicator({ compact }: { compact?: boolean }) {
  const { activeStep } = useWizard();
  const { colors } = useTheme();
  const primary = colors.primary ?? '#7C3AED';
  const [scaleAnim] = useState(() => new Animated.Value(1));
  const [flowPosition] = useState(() => new Animated.Value(0));
  const previousStepRef = useRef(0);

  useEffect(() => {
    scaleAnim.setValue(0.92);
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      tension: 120,
      friction: 10,
    }).start();
  }, [activeStep, scaleAnim]);

  useEffect(() => {
    const prev = previousStepRef.current;
    if (prev !== activeStep) {
      Animated.timing(flowPosition, {
        toValue: activeStep,
        duration: FLOW_DURATION,
        useNativeDriver: true,
      }).start(() => {
        previousStepRef.current = activeStep;
      });
    }
  }, [activeStep, flowPosition]);

  return (
    <View
      style={[styles.stepIndicator, compact && styles.stepIndicatorCompact]}
    >
      {WIZARD_STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const lineProgress =
          index === 0
            ? flowPosition.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 1],
              })
            : index === 1
              ? flowPosition.interpolate({
                  inputRange: [1, 2],
                  outputRange: [0, 1],
                })
              : undefined;
        return (
          <React.Fragment key={step.title}>
            {index > 0 && (
              <View
                style={[
                  styles.stepLineTrack,
                  compact && styles.stepLineTrackCompact,
                  { backgroundColor: 'rgba(0,0,0,0.12)' },
                ]}
              >
                <Animated.View
                  style={[
                    styles.stepLineFill,
                    { backgroundColor: primary },
                    lineProgress !== undefined && {
                      transform: [{ scaleX: lineProgress }],
                    },
                  ]}
                />
              </View>
            )}
            <View style={[styles.stepItem, compact && styles.stepItemCompact]}>
              <Animated.View
                style={[
                  styles.stepCircle,
                  {
                    backgroundColor: isActive ? primary : 'rgba(0,0,0,0.08)',
                  },
                  isActive && { transform: [{ scale: scaleAnim }] },
                ]}
              >
                <Ionicons
                  name={step.icon}
                  size={22}
                  color={isActive ? '#FFFFFF' : '#6B7280'}
                />
              </Animated.View>
              <Text
                style={[
                  styles.stepLabel,
                  compact && styles.stepLabelCompact,
                  { color: isActive ? primary : '#9CA3AF' },
                ]}
                numberOfLines={2}
              >
                {step.label}
              </Text>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );
}

function WizardFooter() {
  const { nextStep, previousStep, isFirstStep, isLastStep } = useWizard();
  const onClose = useContext(WizardModalCloseContext);
  const { colors } = useTheme();
  const primary = colors.primary ?? '#7C3AED';

  const handleNext = () => {
    if (isLastStep) {
      onClose?.();
    } else {
      nextStep();
    }
  };

  return (
    <View style={styles.footer}>
      {!isFirstStep ? (
        <Pressable
          onPress={previousStep}
          style={({ pressed }) => [pressed && { opacity: 0.7 }]}
        >
          <Text style={[styles.footerBack, { color: primary }]}>Back</Text>
        </Pressable>
      ) : (
        <View />
      )}
      <Pressable
        onPress={handleNext}
        style={({ pressed }) => [
          styles.footerNext,
          { backgroundColor: primary },
          pressed && { opacity: 0.9 },
        ]}
      >
        <Text style={styles.footerNextText}>
          {isLastStep ? 'Get started' : 'Next'}
        </Text>
      </Pressable>
    </View>
  );
}

const STEP_ANIM_DURATION = 280;

function StepContent({
  contentTitle,
  contentBody,
}: {
  contentTitle: string;
  contentBody: string;
}) {
  const { colors } = useTheme();
  const [opacity] = useState(() => new Animated.Value(0));
  const [translateY] = useState(() => new Animated.Value(10));

  useEffect(() => {
    opacity.setValue(0);
    translateY.setValue(10);
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: STEP_ANIM_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: STEP_ANIM_DURATION,
        useNativeDriver: true,
      }),
    ]).start();
  }, [contentTitle, opacity, translateY]);

  return (
    <Animated.View
      style={[
        styles.stepContent,
        {
          opacity,
          transform: [{ translateY }],
        },
      ]}
    >
      <Text style={[styles.stepContentTitle, { color: colors.text }]}>
        {contentTitle}
      </Text>
      <Text
        style={[
          styles.stepContentBody,
          { color: colors.secondary ?? '#6B7280' },
        ]}
      >
        {contentBody}
      </Text>
    </Animated.View>
  );
}

function Step1() {
  useWizard();
  return (
    <StepContent
      contentTitle={WIZARD_STEPS[0].contentTitle}
      contentBody={WIZARD_STEPS[0].contentBody}
    />
  );
}

function Step2() {
  useWizard();
  return (
    <StepContent
      contentTitle={WIZARD_STEPS[1].contentTitle}
      contentBody={WIZARD_STEPS[1].contentBody}
    />
  );
}

function Step3() {
  useWizard();
  return (
    <StepContent
      contentTitle={WIZARD_STEPS[2].contentTitle}
      contentBody={WIZARD_STEPS[2].contentBody}
    />
  );
}

export interface ColorAnalysisWizardModalProps {
  visible: boolean;
  onClose: () => void;
}

const MODAL_FIXED_HEIGHT = 340;

const ColorAnalysisWizardModal: React.FC<ColorAnalysisWizardModalProps> = ({
  visible,
  onClose,
}) => {
  const { colors } = useTheme();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
  const modalWidth = Math.min(400, (screenWidth || windowWidth) * 0.85);
  const modalHeight = Math.min(MODAL_FIXED_HEIGHT, windowHeight * 0.75);
  const compactSteps = isMobile || modalWidth < 360;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent={Platform.OS !== 'web'}
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[
            styles.modalBox,
            {
              backgroundColor: colors.card ?? '#FFFFFF',
              width: modalWidth,
              height: modalHeight,
              ...(Platform.OS === 'web' && {
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              }),
            },
          ]}
          onStartShouldSetResponder={() => true}
        >
          <View style={styles.body}>
            <WizardModalCloseContext.Provider value={onClose}>
              <Wizard
                startIndex={0}
                header={
                  <View
                    style={[
                      styles.header,
                      compactSteps && styles.headerCompact,
                    ]}
                  >
                    <StepIndicator compact={compactSteps} />
                  </View>
                }
                footer={<WizardFooter />}
                onStepChange={() => {}}
              >
                <Step1 />
                <Step2 />
                <Step3 />
              </Wizard>
            </WizardModalCloseContext.Provider>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalBox: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
  },
  header: {
    paddingTop: 32,
    paddingBottom: 16,
    width: '100%',
  },
  headerCompact: {
    paddingTop: 28,
    paddingBottom: 12,
  },
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  stepIndicatorCompact: {
    paddingHorizontal: 4,
  },
  stepLine: {
    flex: 1,
    height: 2,
    marginHorizontal: 4,
    marginTop: 20,
    borderRadius: 1,
  },
  stepLineTrack: {
    flex: 1,
    height: 2,
    marginHorizontal: 4,
    marginTop: 20,
    borderRadius: 1,
    overflow: 'hidden',
    minWidth: 8,
  },
  stepLineTrackCompact: {
    marginHorizontal: 2,
    flexShrink: 1,
  },
  stepLineFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    marginLeft: '-50%',
    borderRadius: 1,
  },
  stepItem: {
    alignItems: 'center',
    minWidth: 80,
    paddingHorizontal: 4,
  },
  stepItemCompact: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 6,
  },
  stepCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
    textAlign: 'center',
    width: '100%',
  },
  stepLabelCompact: {
    fontSize: 11,
    lineHeight: 14,
    flexShrink: 1,
    paddingHorizontal: 2,
  },
  body: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 24,
    minHeight: 140,
  },
  stepContent: {
    paddingVertical: 16,
  },
  stepContentTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  stepContentBody: {
    fontSize: 15,
    lineHeight: 22,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  footerBack: {
    fontSize: 16,
    fontWeight: '600',
  },
  footerNext: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  footerNextText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

export default ColorAnalysisWizardModal;
